import React from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, FormGroup, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { get, put, post, del, getAuthHeaders } from '../../utils/api';

const enc = (s) => encodeURIComponent(String(s || ''));

const STATUS_COLOR = {
  scheduled: 'info', sending: 'warning', sent: 'success', cancelled: 'default', failed: 'error',
};

/**
 * Per-event email setup: content params + live preview + Brevo test send + scheduling.
 * All sends are safe — test goes via Brevo to the admin; scheduled sends only fire when the
 * backend's CALENDAR_EMAIL_SEND_ENABLED flag is on (Testing list only during validation).
 */
export default function EventEmailDialog({ open, onClose, event }) {
  const eventKey = event?.event_key || '';
  const [form, setForm] = React.useState({ image_url: '', description: '', subject_override: '', beneficiaries_text: '', closing_line: '' });
  const [defaults, setDefaults] = React.useState({});
  const [previewHtml, setPreviewHtml] = React.useState('');
  const [previewSubject, setPreviewSubject] = React.useState('');
  const [schedules, setSchedules] = React.useState([]);
  const [daysBefore, setDaysBefore] = React.useState(3);
  const [sendTime, setSendTime] = React.useState('09:00');
  const [lists, setLists] = React.useState([]);
  const [selectedLists, setSelectedLists] = React.useState([]);
  const [testEmail, setTestEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');

  const loadPreview = React.useCallback(async () => {
    const res = await get(`/api/manage/calendar-events/${enc(eventKey)}/email-preview`);
    setPreviewHtml(res?.html || '');
    setPreviewSubject(res?.subject || '');
  }, [eventKey]);

  const loadAll = React.useCallback(async () => {
    if (!eventKey) return;
    setLoading(true); setError(''); setOk('');
    try {
      const c = await get(`/api/manage/calendar-events/${enc(eventKey)}/email-content`);
      const content = c?.content || {};
      const dflt = c?.defaults || {};
      setDefaults(dflt);
      // Prefill with the saved value if present, else the resolved default (editable; clear for auto).
      setForm({
        image_url: content.image_url || dflt.image_url || '',
        description: content.description || '',
        subject_override: content.subject_override || dflt.subject || '',
        beneficiaries_text: content.beneficiaries_text || dflt.beneficiaries_text || '',
        closing_line: content.closing_line || dflt.closing_line || '',
      });
      setTestEmail(c?.requester_email || '');
      // Mailing lists the scheduler may choose from (allowlist); pre-check the configured defaults.
      try {
        const ll = await get('/api/manage/calendar-email-lists');
        const allowed = ll?.lists || [];
        setLists(allowed);
        setSelectedLists(allowed.filter((l) => l.is_default).map((l) => String(l.id)));
      } catch { setLists([]); setSelectedLists([]); }
      const s = await get('/api/manage/email-schedules');
      setSchedules((s?.schedules || []).filter((x) => x.event_key === eventKey));
      await loadPreview();
    } catch (e) {
      setError(e?.message || 'Failed to load email setup');
    } finally { setLoading(false); }
  }, [eventKey, loadPreview]);

  React.useEffect(() => { if (open) loadAll(); }, [open, loadAll]);

  const onSave = async () => {
    setSaving(true); setError(''); setOk('');
    try {
      await put(`/api/manage/calendar-events/${enc(eventKey)}/email-content`, {
        image_url: form.image_url.trim(),
        description: form.description.trim(),
        subject_override: form.subject_override.trim() || null,
        beneficiaries_text: form.beneficiaries_text.trim() || null,
        closing_line: form.closing_line.trim() || null,
      });
      await loadPreview();
      setOk('Saved. Preview updated.');
    } catch (e) { setError(e?.message || 'Failed to save'); } finally { setSaving(false); }
  };

  const onTest = async () => {
    setBusy('test'); setError(''); setOk('');
    try {
      const res = await post(`/api/manage/calendar-events/${enc(eventKey)}/email-test`, testEmail.trim() ? { email: testEmail.trim() } : {});
      setOk(`Test sent to ${res?.sent_to} (subject: ${res?.subject}).`);
    } catch (e) { setError(e?.message || 'Failed to send test'); } finally { setBusy(''); }
  };

  const refreshSchedules = async () => {
    const s = await get('/api/manage/email-schedules');
    setSchedules((s?.schedules || []).filter((x) => x.event_key === eventKey));
  };

  const toggleList = (id) => setSelectedLists((prev) => (
    prev.includes(String(id)) ? prev.filter((x) => x !== String(id)) : [...prev, String(id)]
  ));

  const listLabel = React.useCallback((id) => {
    const m = lists.find((l) => String(l.id) === String(id));
    if (!m) return String(id);
    return m.count != null ? `${m.name} (${m.count})` : m.name;
  }, [lists]);

  const onSchedule = async (sendNow) => {
    if (!selectedLists.length) { setError('Select at least one mailing list.'); return; }
    setBusy(sendNow ? 'now' : 'schedule'); setError(''); setOk('');
    try {
      await post(`/api/manage/calendar-events/${enc(eventKey)}/email-schedule`,
        sendNow
          ? { send_now: true, list_ids: selectedLists }
          : { days_before: Number(daysBefore), send_time: sendTime, list_ids: selectedLists });
      await refreshSchedules();
      const listNote = selectedLists.map(listLabel).join(', ');
      setOk(sendNow
        ? `Send-now scheduled to ${listNote} (fires in ~5 min — cancel below to stop).`
        : `Saved: sends ${daysBefore} day(s) before the event at ${sendTime} Central to ${listNote}.`);
    } catch (e) { setError(e?.message || 'Failed to schedule'); } finally { setBusy(''); }
  };

  const onCancel = async (id) => {
    setError(''); setOk('');
    try { await del(`/api/manage/email-schedule/${id}`); await refreshSchedules(); setOk('Schedule cancelled.'); }
    catch (e) { setError(e?.message || 'Failed to cancel'); }
  };

  const [posterBusy, setPosterBusy] = React.useState(false);
  const downloadPoster = async () => {
    setPosterBusy(true); setError(''); setOk('');
    try {
      let base = process.env.REACT_APP_API_BASE_URL || '';
      if (!base && /localhost|127\.0\.0\.1/.test(window.location.hostname)) base = 'http://localhost:5001';
      base = base.replace(/\/$/, '');
      const res = await fetch(`${base}/api/manage/calendar-events/${enc(eventKey)}/poster.png?download=1`,
        { credentials: 'include', headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Image failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${eventKey}-poster.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      setOk('Poster image downloaded.');
    } catch (e) { setError(e?.message || 'Failed to download image'); } finally { setPosterBusy(false); }
  };

  const fmt = (iso) => { try { const d = new Date(iso); return isNaN(d) ? String(iso || '—') : d.toLocaleString(); } catch { return iso; } };

  // Label a "days before" option with the resulting calendar date + weekday, e.g. "3 days before — Wed, Jul 8".
  const daysBeforeLabel = (i) => {
    const base = i === 0 ? 'Day of event' : `${i} day${i > 1 ? 's' : ''} before`;
    const [y, m, d] = String(event?.event_date || '').split('-').map(Number);
    if (!y || !m || !d) return base;
    const dt = new Date(y, m - 1, d); dt.setDate(dt.getDate() - i);
    return `${base} — ${dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { width: '96vw' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        Event Email — {event?.title || ''}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
        {ok ? <Alert severity="success" sx={{ mb: 1.5 }}>{ok}</Alert> : null}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Left: form + schedule */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Content</Typography>
              <Stack spacing={1.5}>
                <TextField label="Event image URL" size="small" fullWidth value={form.image_url}
                  onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                  placeholder={defaults.image_url || 'https://…'}
                  helperText="Prefilled from the mapped product image; clear to use it automatically." />
                <TextField label="Importance of the Festival/Event (description)" size="small" fullWidth multiline minRows={4}
                  value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                <TextField label="Subject (optional override)" size="small" fullWidth value={form.subject_override}
                  onChange={(e) => setForm((f) => ({ ...f, subject_override: e.target.value }))}
                  placeholder={defaults.subject || "{date}: {event name}"}
                  helperText="Prefilled with the auto subject; clear to keep it automatic." />
                <TextField label="Beneficiaries (optional override)" size="small" fullWidth value={form.beneficiaries_text}
                  onChange={(e) => setForm((f) => ({ ...f, beneficiaries_text: e.target.value }))}
                  placeholder={defaults.beneficiaries_text || 'No linked Poshaka product'}
                  helperText="Prefilled from Poshaka benefit sets; clear to keep it automatic." />
                <TextField label="Closing line (optional override)" size="small" fullWidth value={form.closing_line}
                  onChange={(e) => setForm((f) => ({ ...f, closing_line: e.target.value }))}
                  placeholder={defaults.closing_line || 'All are welcome…'}
                  helperText="Clear to use the default closing line." />
                <Box>
                  <Button variant="contained" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save content'}</Button>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Test send (via Brevo — safe)</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" label="Test to (blank = you)" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} sx={{ flex: 1 }} />
                <Button variant="outlined" onClick={onTest} disabled={busy === 'test'}>{busy === 'test' ? 'Sending…' : 'Send test'}</Button>
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Schedule (GetResponse)</Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Mailing lists</Typography>
              {lists.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No mailing lists available.</Typography>
              ) : (
                <FormGroup sx={{ mb: 1.5 }}>
                  {lists.map((l) => (
                    <FormControlLabel key={l.id} sx={{ my: -0.25 }}
                      control={<Checkbox size="small" checked={selectedLists.includes(String(l.id))} onChange={() => toggleList(l.id)} />}
                      label={(
                        <Typography variant="body2">
                          {l.name} <Typography component="span" variant="caption" color="text.secondary">({l.count != null ? l.count : '?'} recipients){l.is_default ? ' · default' : ''}</Typography>
                        </Typography>
                      )} />
                  ))}
                </FormGroup>
              )}

              <Stack direction="row" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <TextField select size="small" label="When to send" value={daysBefore}
                  onChange={(e) => setDaysBefore(e.target.value)} sx={{ width: 260 }}>
                  {Array.from({ length: 15 }, (_, i) => <MenuItem key={i} value={i}>{daysBeforeLabel(i)}</MenuItem>)}
                </TextField>
                <TextField type="time" size="small" label="Send time (Central)" value={sendTime}
                  onChange={(e) => setSendTime(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                <Button variant="contained" onClick={() => onSchedule(false)}
                  disabled={busy === 'schedule' || !selectedLists.length || schedules.filter((s) => ['scheduled', 'sending'].includes(s.status)).length >= 3}>
                  {busy === 'schedule' ? 'Saving…' : 'Save schedule'}
                </Button>
                <Tooltip title="Queues a send ~5 minutes out; cancel within that window to stop.">
                  <span><Button variant="outlined" color="warning" onClick={() => onSchedule(true)} disabled={busy === 'now' || !selectedLists.length}>Send now (5 min)</Button></span>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Up to 3 active schedules per event.
              </Typography>

              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {schedules.length === 0 ? <Typography variant="body2" color="text.secondary">No schedules yet.</Typography> : null}
                {schedules.map((s) => (
                  <Stack key={s.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, borderBottom: '1px solid #eee' }}>
                    <Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} variant="outlined" />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {s.is_send_now ? 'Send now' : `${s.days_before}d before`} · {fmt(s.send_at)}
                      {Array.isArray(s.list_ids) && s.list_ids.length ? ` · ${s.list_ids.map(listLabel).join(', ')}` : ''}
                      {s.error_text ? ` · ${s.error_text}` : ''}
                    </Typography>
                    {s.status === 'scheduled' ? (
                      <Button size="small" color="error" variant="outlined" onClick={() => onCancel(s.id)}
                        disabled={busy === 'cancel'} sx={{ minWidth: 0, px: 1 }}>Cancel</Button>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Right: preview */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Preview</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Download this as a share-ready image (WhatsApp / Facebook)">
                    <span>
                      <Button size="small" variant="outlined" onClick={downloadPoster} disabled={posterBusy}>
                        {posterBusy ? 'Generating…' : 'Download image'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Refresh preview"><IconButton size="small" onClick={loadPreview}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Subject: {previewSubject}</Typography>
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', height: 620, bgcolor: '#fff' }}>
                <iframe title="Email preview" srcDoc={previewHtml} style={{ width: '100%', height: '100%', border: 0 }} />
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
